import { ArrowDownIcon, ArrowUpIcon, ExtendedMenuIcon } from '@synergy/theme';
import { Children, Component, MouseEvent, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Icon, IconButton, Menu, MenuList, Theme } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import uniqid from 'uniqid';

import { PoolButton } from '../button/pool-button';
import { PoolMenuItem } from './pool-menu-item';

const styles = (theme: Theme) =>
    createStyles({
        root: {
            '& li > div': {
                color: '#21272E',
            },
            '& > ul': {
                padding: 0,
            },
        },
        button: {
            backgroundColor: '#6DBFCF',
        },
        left: {
            marginLeft: '-100px',
        },
        right: {
            marginLeft: '100px',
        },
        extendedMenuIcon: {
            fontSize: 20,
            color: '#21272E',
        },
        popoverPaperRoot: {
            marginTop: theme.spacing(),
        },
        popoverPaperLeft: {
            marginLeft: theme.spacing(),
        },
        popoverPaperRight: {
            marginRight: theme.spacing(),
        },
        ...typeStyles(),
    });

const typeStyles = () =>
    createStyles({
        default: {
            '& svg': {
                fontSize: 16,
            },
        },
        toolbarDropdown: {
            '&:active': {
                backGroundColor: '#6DBFCF',
            },
            '& li.Mui-selected:hover': {
                backgroundColor: '#6DBFCF',
            },
            '& li:hover': {
                backgroundColor: '#42ACC1',
            },
            '& li > span': {
                fontSize: '15px',
            },
            '& li:hover > span': {
                color: '#FFFFFF',
            },
        },
    });

export interface PoolMenuProps extends WithStyles<typeof styles> {
    icon?: ReactNode;
    title?: ReactNode;
    styleType?: keyof ReturnType<typeof typeStyles>;
    children?: ReactNode;
    isSubmenu?: boolean;
    submenuPosition?: 'left' | 'right';
    menuPosition?: 'left' | 'right';
    disabled?: boolean;
}

interface ActionMenuState {
    anchorEl?: HTMLElement;
}

export const PoolMenu = withStyles(styles)(
    class PoolMenuBase extends Component<PoolMenuProps, ActionMenuState> {
        public override state: ActionMenuState = {
            anchorEl: undefined,
        };

        private getDisplayClass(props: PoolMenuProps): string {
            if (props.styleType) {
                return props.classes[props.styleType];
            }
            return props.classes.default;
        }

        private getClassNames(props: PoolMenuProps) {
            return `${this.getDisplayClass(props)}`;
        }

        private handleClick = (event: MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            this.setState({ anchorEl: event.currentTarget });
        };

        private handleClose = (event: MouseEvent<HTMLElement>) => {
            if (!this.props.isSubmenu) {
                event.stopPropagation();
            }
            this.setState({ anchorEl: undefined });
        };

        private renderButton = () => {
            const { icon, title, classes } = this.props;
            const anchorEl = this.state.anchorEl;

            let buttonIcon: ReactNode = (
                <ExtendedMenuIcon
                    className={classes.extendedMenuIcon}
                    style={this.state.anchorEl && { rotate: '180deg' }}
                />
            );
            buttonIcon = typeof icon === 'string' ? <Icon className={icon} /> : buttonIcon;
            buttonIcon = typeof icon === 'object' ? icon : buttonIcon;

            if (title) {
                return (
                    <PoolButton
                        styleType="toolbar"
                        onClick={this.handleClick}
                        className={anchorEl ? classes.button : ''}
                        startIcon={buttonIcon}
                        endIcon={anchorEl ? <ArrowUpIcon /> : <ArrowDownIcon />}
                        disabled={this.props.disabled}
                    >
                        {typeof title === 'string' ? <FormattedMessage id={title} /> : title}
                    </PoolButton>
                );
            }
            return <IconButton onClick={this.handleClick}>{buttonIcon}</IconButton>;
        };

        public override render() {
            const { icon, title, classes, children, isSubmenu, menuPosition, submenuPosition } = this.props;
            const { anchorEl } = this.state;
            const paperStyles = `${classes.popoverPaperRoot} +
                ${menuPosition === 'right' ? classes.popoverPaperRight : classes.popoverPaperLeft || ''}`;

            if (Children.count(children) === 0) {
                return null;
            }

            return (
                <>
                    {isSubmenu ? (
                        <PoolMenuItem icon={icon} title={title || ''} onClick={this.handleClick} isSubMenu={true} />
                    ) : (
                        this.renderButton()
                    )}
                    <Menu
                        id={uniqid()}
                        anchorEl={anchorEl}
                        open={!!anchorEl}
                        onClose={this.handleClose}
                        classes={{ paper: classes.root }}
                        className={isSubmenu ? classes[submenuPosition || 'right'] : ''}
                        PopoverClasses={{
                            paper: paperStyles || '',
                        }}
                        anchorOrigin={{
                            horizontal: this.props.menuPosition ?? 'left',
                            vertical: isSubmenu ? 'center' : 'bottom',
                        }}
                        transformOrigin={{
                            horizontal: this.props.menuPosition ?? 'left',
                            vertical: isSubmenu ? 'center' : 'top',
                        }}
                    >
                        <MenuList
                            classes={{ root: this.props.classes.root }}
                            onClick={this.handleClose}
                            className={this.getClassNames(this.props)}
                        >
                            {children}
                        </MenuList>
                    </Menu>
                </>
            );
        }
    }
);
